/**
 * CreateOutreachButton — appears on case detail pages for flagged UIDs.
 *
 * Shown when the case carries any of:
 *   - DOE_NETWORK_FORENSIC_DISCREPANCY
 *   - HIGH_PRIORITY
 *   - STANDLEY_TIER_ACTIONABLE
 *
 * Pre-fills the outreach creation dialog with agency contact from
 * investigating_agencies[0] + the current UID as the single case_id.
 *
 * If agency[0] has no email but has a webform_url, renders a
 * "Submit via webform" link instead (webforms cannot be Resend-dispatched).
 *
 * Backlog Fix 2 (2026-05-22):
 *   - Tone selector (description_variant: auto/default/formal/cold_case).
 *   - Letter-source dropdown — raw path field only revealed for "Custom".
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Mail, Send, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { createOutreach, fetchOutreachLetterTemplates } from '@/lib/api';

const TRIGGER_FLAGS = new Set([
  'DOE_NETWORK_FORENSIC_DISCREPANCY',
  'HIGH_PRIORITY',
  'STANDLEY_TIER_ACTIONABLE',
]);

// Letter path suggestions — match by flag; users can override.
const LETTER_SUGGESTIONS = {
  DOE_NETWORK_FORENSIC_DISCREPANCY: '/app/backend/content/la_county_me_doe_discrepancy.md',
};

const CUSTOM_TEMPLATE_ID = '__custom__';

export default function CreateOutreachButton({ caseData }) {
  const flags = caseData?.flags || [];
  const hasTriggerFlag = flags.some(f => TRIGGER_FLAGS.has(f));

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Pre-fill from investigating_agencies[0]
  const agency = (caseData?.investigating_agencies || [])[0] || {};
  const hasDirectEmail = !!(agency.email || '').trim();
  const hasWebform = !!(agency.webform_url || '').trim();
  const uid = caseData?.namus_number || caseData?.external_id || '';

  const triggerFlag = flags.find(f => TRIGGER_FLAGS.has(f));
  const defaultLetter = LETTER_SUGGESTIONS[triggerFlag] || '';

  const [form, setForm] = useState({
    letter_path: defaultLetter,
    recipient_email: agency.email || '',
    recipient_name: agency.name || '',
    agency_name: agency.name || '',
    case_ids: uid,
    subject: '',
    priority: 'routine',
    description_variant: 'auto',
    notes: `Created from case detail page for ${uid}. Flag: ${triggerFlag || 'manual'}.`,
  });

  // Letter-source dropdown — null until templates load. The "Custom"
  // entry is appended client-side so the raw path field remains an
  // escape hatch when no curated template fits.
  const [templates, setTemplates] = useState([]);
  const [templateChoice, setTemplateChoice] = useState(
    defaultLetter ? CUSTOM_TEMPLATE_ID : null
  );

  useEffect(() => {
    if (!open) return;
    if (templates.length > 0) return;
    fetchOutreachLetterTemplates()
      .then((res) => {
        const list = (res?.templates || []).filter(t => t.kind === 'template_file');
        setTemplates(list);
        // If no preset path is wired for this flag, default to the
        // standard agency outreach template (when available).
        if (!form.letter_path) {
          const std = list.find(t => t.id === 'agency_outreach_template.md');
          if (std) {
            setForm(prev => ({ ...prev, letter_path: std.letter_path }));
            setTemplateChoice(std.id);
          } else {
            setTemplateChoice(CUSTOM_TEMPLATE_ID);
          }
        }
      })
      .catch(() => {
        // Network/auth issue — fall back to custom path entry.
        setTemplateChoice(CUSTOM_TEMPLATE_ID);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!hasTriggerFlag || !uid) return null;

  // --- Webform-only path ---
  if (!hasDirectEmail && hasWebform) {
    return (
      <Button
        asChild
        variant="outline"
        size="sm"
        className="text-amber-700 border-amber-400 hover:bg-amber-50"
        data-testid="create-outreach-btn"
        title={`${agency.name || 'This agency'} accepts inquiries via webform only`}
      >
        <a href={agency.webform_url} target="_blank" rel="noreferrer">
          <ExternalLink className="w-4 h-4 mr-1" />
          Submit via webform
        </a>
      </Button>
    );
  }

  // --- No direct email AND no webform — disabled with tooltip ---
  if (!hasDirectEmail && !hasWebform) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="opacity-60"
        data-testid="create-outreach-btn"
        title="No agency email or webform available — add one to investigating_agencies[0] first"
      >
        <AlertTriangle className="w-4 h-4 mr-1" />
        No agency contact
      </Button>
    );
  }

  // --- Normal email path ---
  const handleTemplateChange = (value) => {
    setTemplateChoice(value);
    if (value === CUSTOM_TEMPLATE_ID) {
      // Reveal raw path. Don't clobber an existing custom value.
      return;
    }
    const t = templates.find(x => x.id === value);
    if (t) {
      setForm(prev => ({
        ...prev,
        letter_path: t.letter_path,
        // If the template has a paired tone and the user hasn't already
        // overridden it, snap to that tone so what's printed matches
        // what's selected.
        description_variant: t.tone || prev.description_variant,
      }));
    }
  };

  const showRawPathField = templateChoice === CUSTOM_TEMPLATE_ID;

  const handleCreate = async () => {
    const caseIds = form.case_ids.split(/[,\s]+/).filter(Boolean);
    if (!form.letter_path || !form.recipient_email || !form.agency_name || caseIds.length === 0) {
      toast.error('Letter path, recipient email, agency, and case IDs are required');
      return;
    }
    setBusy(true);
    try {
      await createOutreach({
        letter_path: form.letter_path,
        recipient_email: form.recipient_email,
        recipient_name: form.recipient_name || form.agency_name,
        agency_name: form.agency_name,
        case_ids: caseIds,
        subject: form.subject || undefined,
        priority: form.priority,
        description_variant: form.description_variant,
        notes: form.notes || undefined,
      });
      toast.success('Outreach queued for approval — review at /admin/outreach');
      setOpen(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string'
        ? d
        : (d && typeof d === 'object')
          ? (d.message || d.summary || d.error || JSON.stringify(d))
          : (err?.message || 'Failed to create outreach');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700"
        data-testid="create-outreach-btn"
      >
        <Mail className="w-4 h-4 mr-1" />
        Create Outreach
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              New Outreach for {uid}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Letter-source dropdown — abstraction over raw paths. */}
            <div>
              <Label>Letter source *</Label>
              <select
                value={templateChoice || ''}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full border rounded px-2 py-1 mt-1 h-9 bg-white"
                data-testid="letter-template-select"
              >
                {templateChoice === null && (
                  <option value="" disabled>Loading templates…</option>
                )}
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
                <option value={CUSTOM_TEMPLATE_ID}>Custom path…</option>
              </select>
              {!showRawPathField && templateChoice !== null && (
                <p className="text-xs text-slate-500 mt-1 font-mono truncate">
                  {form.letter_path || '(no path set)'}
                </p>
              )}
            </div>
            {showRawPathField && (
              <div>
                <Label>Custom letter path *</Label>
                <Input
                  value={form.letter_path}
                  onChange={(e) => setForm({ ...form, letter_path: e.target.value })}
                  placeholder="/app/backend/content/your_letter.md"
                  data-testid="prefilled-letter-path"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must be under /app/backend/content/
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Recipient name</Label>
                <Input
                  value={form.recipient_name}
                  onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                  data-testid="prefilled-recipient-name"
                />
              </div>
              <div>
                <Label>Recipient email *</Label>
                <Input
                  type="email"
                  value={form.recipient_email}
                  onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                  data-testid="prefilled-recipient-email"
                />
              </div>
            </div>
            <div>
              <Label>Agency name *</Label>
              <Input
                value={form.agency_name}
                onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                data-testid="prefilled-agency"
              />
            </div>
            <div>
              <Label>Case IDs (comma or space separated) *</Label>
              <Input
                value={form.case_ids}
                onChange={(e) => setForm({ ...form, case_ids: e.target.value })}
                data-testid="prefilled-case-ids"
              />
              <p className="text-xs text-slate-500 mt-1">
                Currently populated with {uid}. Add related UIDs (space or comma separated) to cover multiple cases in one letter.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Subject (optional)</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Auto-generated"
                />
              </div>
              <div>
                <Label>Tone</Label>
                <select
                  value={form.description_variant}
                  onChange={(e) => setForm({ ...form, description_variant: e.target.value })}
                  className="w-full border rounded px-2 py-1 mt-1 h-9 bg-white"
                  data-testid="tone-select"
                >
                  <option value="auto">Auto (by agency)</option>
                  <option value="default">Default</option>
                  <option value="formal">Formal</option>
                  <option value="cold_case">Cold case</option>
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full border rounded px-2 py-1 mt-1 h-9 bg-white"
                >
                  <option value="routine">Routine</option>
                  <option value="priority">Priority</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 rounded p-2">
              <strong>Heads-up:</strong> this creates a <code>pending_approval</code> record.
              It will NOT send until you explicitly approve it from the outreach queue.
              The 14-day follow-up clock starts the moment you approve.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="prefilled-outreach-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Queue for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
